if (!customElements.get('sticky-feature-cta')) {
	class StickyFeatureCta extends HTMLElement {
		constructor() {
			super()
			this.sectionId = this.dataset.sectionId
			this.contentSections = []
			this.imageGroups = []
			this.paginationBullets = []
			this.swipers = []
			this.observer = null
			this.sectionObserver = null
			this.isInitialized = false
			this.reinitTimeout = null
		}

		connectedCallback() {
			if (document.readyState === 'loading') {
				document.addEventListener('DOMContentLoaded', () => this.init())

				if (Shopify?.designMode) {
					this.designModeLoadListener = event => {
						if (event.target.id === `shopify-section-${this.sectionId}`) {
							this.init()
						}
					}
					document.addEventListener('shopify:section:load', this.designModeLoadListener)
				}
			} else {
				this.init()
			}
		}

		init() {
			if (this.isInitialized) return

			this.cacheElements()
			if (!this.validateElements()) {
				return
			}

			this.initSwipers()
			this.setupIntersectionObserver()
			this.isInitialized = true

			if (Shopify?.designMode) {
				this.setupSectionObserver()
			}
		}

		cacheElements() {
			this.contentSections = Array.from(this.querySelectorAll('.js-content-section'))
			this.imageGroups = Array.from(this.querySelectorAll('.js-sticky-image-group'))
			this.paginationBullets = Array.from(this.querySelectorAll('.js-pagination-bullet'))
		}

		validateElements() {
			return (
				this.contentSections.length > 0 &&
				this.imageGroups.length <= this.contentSections.length &&
				this.paginationBullets.length === this.contentSections.length
			)
		}

		initSwipers() {
			this.destroySwipers()
			if (typeof Swiper === 'undefined') return

			this.querySelectorAll('.js-feature-swiper').forEach(el => {
				const slides = el.querySelectorAll('.swiper-slide').length
				if (slides < 2) return
				const swiper = new Swiper(el, {
					loop: false,
					slidesPerView: 1,
					spaceBetween: 0,
					observer: true,
					observeParents: true,
					navigation: {
						nextEl: el.querySelector('.swiper-button-next'),
						prevEl: el.querySelector('.swiper-button-prev'),
					},
					pagination: {
						el: el.querySelector('.swiper-pagination'),
						clickable: true,
					},
				})
				this.swipers.push(swiper)
			})

			this.querySelectorAll('.js-feature-swiper-mobile').forEach(el => {
				const slides = el.querySelectorAll('.swiper-slide').length
				if (slides < 2) return
				const swiper = new Swiper(el, {
					loop: false,
					slidesPerView: 1,
					spaceBetween: 0,
					observer: true,
					observeParents: true,
					pagination: {
						el: el.querySelector('.swiper-pagination'),
						clickable: true,
					},
				})
				this.swipers.push(swiper)
			})
		}

		destroySwipers() {
			this.swipers.forEach(s => {
				try { s.destroy(true, true) } catch (e) {}
			})
			this.swipers = []
		}

		setupIntersectionObserver() {
			const options = {
				threshold: 0.5,
				rootMargin: '0px',
			}

			this.observer = new IntersectionObserver(
				(entries) => {
					let mostVisibleSection = null
					let maxRatio = 0

					entries.forEach((entry) => {
						if (entry.intersectionRatio > maxRatio) {
							maxRatio = entry.intersectionRatio
							mostVisibleSection = entry.target
						}
					})

					if (mostVisibleSection && maxRatio >= 0.5) {
						this.handleSectionIntersection(mostVisibleSection)
					}
				},
				options
			)

			this.contentSections.forEach((section) => {
				this.observer.observe(section)
			})

			const firstSection = this.contentSections[0]
			if (firstSection) {
				this.setActiveGroup(firstSection.dataset.index)
				this.setActivePagination(firstSection.dataset.index)
				this.updateColourScheme(firstSection.dataset.colourScheme, firstSection)
			}
		}

		handleSectionIntersection(section) {
			const sectionIndex = parseInt(section.dataset.index, 10)
			this.setActiveGroup(sectionIndex)
			this.setActivePagination(sectionIndex)
			this.updateColourScheme(section.dataset.colourScheme, section)
		}

		setActiveGroup(index) {
			this.imageGroups.forEach(group => group.classList.remove('active'))
			if (this.imageGroups[index]) {
				this.imageGroups[index].classList.add('active')
			}
		}

		setActivePagination(index) {
			this.paginationBullets.forEach(bullet => bullet.classList.remove('active'))
			if (this.paginationBullets[index]) {
				this.paginationBullets[index].classList.add('active')
			}
		}

		updateColourScheme(colourScheme, section) {
			this.classList.remove('t-light', 't-dark')

			if (colourScheme) {
				this.classList.add(`t-${colourScheme}`)
			}

			const bg = section && section.dataset.customBg ? section.dataset.customBg : ''
			const text = section && section.dataset.customText ? section.dataset.customText : ''

			if (bg) {
				this.style.setProperty('--sfc-custom-bg', bg)
				this.classList.add('has-custom-bg')
			} else {
				this.style.removeProperty('--sfc-custom-bg')
				this.classList.remove('has-custom-bg')
			}

			if (text) {
				this.style.setProperty('--sfc-custom-text', text)
				this.classList.add('has-custom-text')
			} else {
				this.style.removeProperty('--sfc-custom-text')
				this.classList.remove('has-custom-text')
			}
		}

		setupSectionObserver() {
			this.sectionObserver = new MutationObserver(() => {
				this.reinitializeDebounced()
			})

			this.sectionObserver.observe(this, {
				childList: true,
				subtree: true,
				attributes: true,
				attributeFilter: ['data-index', 'class'],
			})
		}

		reinitializeDebounced() {
			if (this.reinitTimeout) {
				clearTimeout(this.reinitTimeout)
			}
			this.reinitTimeout = setTimeout(() => {
				this.reinitialize()
			}, 200)
		}

		reinitialize() {
			if (this.observer) {
				this.observer.disconnect()
			}

			this.cacheElements()
			if (this.validateElements()) {
				this.initSwipers()
				this.setupIntersectionObserver()
			}
		}

		disconnectedCallback() {
			if (this.observer) {
				this.observer.disconnect()
			}
			if (this.sectionObserver) {
				this.sectionObserver.disconnect()
			}
			if (this.designModeLoadListener && Shopify?.designMode) {
				document.removeEventListener('shopify:section:load', this.designModeLoadListener)
			}
			if (this.reinitTimeout) {
				clearTimeout(this.reinitTimeout)
			}
			this.destroySwipers()
			this.isInitialized = false
		}
	}

	customElements.define('sticky-feature-cta', StickyFeatureCta)
}
